import { useEffect, useMemo, useRef } from "react"
import { useOptimization } from "hooks/optimizer"
import { SelectedTypedInstrument } from "helpers/types/instruments/selected"
import { IsoDate } from "helpers/types/dates"
import { OptimizedPositionSet, OptimizationConfiguration } from "./types"
import { usePortfolioHoldings } from "hooks/rebalancer"
import { calculateCurrentMarketValues } from "../components/helpers/configuration"
import { ERROR, OK } from "helpers/data-statuses"

export const useAutoRunOptimization = (
  instrument: SelectedTypedInstrument | null,
  modelId: string,
  endDate: IsoDate,
  base: SelectedTypedInstrument | null,
  setOptimizeConfiguration: (configuration: OptimizationConfiguration) => void,
  optimizeConfiguration: OptimizationConfiguration | null,
  setComparisonInstrument: (instrument: OptimizedPositionSet | null) => void,
  isOptimizeExperimentsEnabled: boolean,
  isOptimizedExperimentsDrawerOpen: boolean,
): void => {
  const [portfolioHoldings, portfolioHoldingsStatus] = usePortfolioHoldings(instrument, modelId, endDate)
  const portfolioMarketValues = useMemo(() => {
    if (portfolioHoldingsStatus !== OK) {
      return { longMarketValue: 0, shortMarketValue: 0 }
    }
    return calculateCurrentMarketValues(portfolioHoldings)
  }, [portfolioHoldings, portfolioHoldingsStatus])
  const forecastDataRef = useRef<string | null>(null)
  const forecastDataLoadingRef = useRef<boolean>(false)
  const forecast = optimizeConfiguration?.forecast && optimizeConfiguration.forecast !== "none"
    ? optimizeConfiguration.forecast : null

  const { runOptimization, getConfiguration, optimizedPositionSet, getForecastData, forecastDataStatus, forecastData } = useOptimization(
    instrument,
    modelId,
    endDate,
    base,
    { overrideConfiguration: optimizeConfiguration },
    portfolioMarketValues,
  )

  useEffect(() => {
    forecastDataRef.current = null
  }, [forecast])

  useEffect(() => {
    const canRunOptimization = Boolean(
      isOptimizeExperimentsEnabled
      && optimizeConfiguration
      && endDate
      && instrument
      && modelId
      && portfolioHoldingsStatus === OK,
    )

    if (!canRunOptimization) {
      return
    }

    if (optimizeConfiguration?.on === endDate || isOptimizedExperimentsDrawerOpen) {
      return
    }

    if (forecast && forecastDataRef.current === null) {
      forecastDataRef.current = forecast
      forecastDataLoadingRef.current = true
      getForecastData()
      return
    }

    if (forecastDataLoadingRef.current) {
      return
    }

    runOptimization()
    const configuration = getConfiguration()
    setOptimizeConfiguration(configuration)
  }, [
    runOptimization,
    setOptimizeConfiguration,
    endDate,
    optimizeConfiguration,
    getConfiguration,
    instrument,
    modelId,
    base,
    isOptimizeExperimentsEnabled,
    isOptimizedExperimentsDrawerOpen,
    portfolioHoldingsStatus,
    forecast,
    forecastDataStatus,
    getForecastData,
  ])

  useEffect(() => {
    if (isOptimizedExperimentsDrawerOpen) {
      return
    }

    if (forecastDataLoadingRef.current && forecastData && forecastDataStatus === OK) {
      forecastDataLoadingRef.current = false
      runOptimization()
      const configuration = getConfiguration()
      setOptimizeConfiguration(configuration)
    } else if (forecastDataStatus === ERROR) {
      forecastDataLoadingRef.current = false
    }
  }, [forecastData, forecastDataStatus, runOptimization, getConfiguration, setOptimizeConfiguration, isOptimizedExperimentsDrawerOpen])

  useEffect(() => {
    if (!isOptimizeExperimentsEnabled || !optimizedPositionSet) {
      return
    }

    setComparisonInstrument(optimizedPositionSet)
  }, [optimizedPositionSet, setComparisonInstrument, isOptimizeExperimentsEnabled])
}
